import { create } from "zustand";
import { createJSONStorage, type PersistOptions } from "zustand/middleware";
import { persist } from "zustand/middleware";
import { useStore } from "zustand/index";
import { keyColumnVisibility } from "./StoreNames.ts";

/**
 * Store state for column visibility preferences
 * Maps tableName to array of visible column keys
 */
interface ColumnVisibilityState {
  /** Map of tableName to array of selected column keys */
  tableColumnVisibility: Record<string, React.Key[]>;
  
  /**
   * Initialize table with default keys if not exists
   * @param tableName - The name of the table
   * @param defaultKeys - Default keys to use if table has no entry
   */
  initializeTable: (tableName: string, defaultKeys: React.Key[]) => boolean;
  
  /**
   * Set selected keys for a table
   * @param tableName - The name of the table
   * @param selectedKeys - Array of selected column keys
   */
  setSelectedKeys: (tableName: string, selectedKeys: React.Key[]) => void;
  
  /**
   * Clear all stored column visibility preferences
   */
  clearAll: () => void;
}

const persistOptions: PersistOptions<ColumnVisibilityState> = {
  storage: createJSONStorage<ColumnVisibilityState>(() => localStorage),
  name: keyColumnVisibility,
};

const ColumnVisibilityStore = create<ColumnVisibilityState>()(
  persist(
    (set, get) => ({
      tableColumnVisibility: {},
      
      initializeTable: (tableName: string, defaultKeys: React.Key[]) => {
        const state = get();
        const stored = state.tableColumnVisibility[tableName];
        
        // If no entry exists, initialize with defaults
        if (!stored) {
          set((prev) => ({
            tableColumnVisibility: {
              ...prev.tableColumnVisibility,
              [tableName]: defaultKeys,
            },
          }));
          return true;
        }
        return false;
      },
      
      setSelectedKeys: (tableName: string, selectedKeys: React.Key[]) => {
        set((prev) => ({
          tableColumnVisibility: {
            ...prev.tableColumnVisibility,
            [tableName]: selectedKeys,
          },
        }));
      },
      
      clearAll: () => {
        set({ tableColumnVisibility: {} });
      },
    }),
    persistOptions,
  ),
);

// Hook to get store methods and state
const useColumnVisibilityStore = () => {
  return useStore(ColumnVisibilityStore);
};

// Hook to get selected keys for a specific table (reactive)
const useSelectedKeys = (tableName: string, defaultKeys: React.Key[]) => {
  return useStore(
    ColumnVisibilityStore,
    (state) => state.tableColumnVisibility[tableName] ?? defaultKeys
  );
};

export { useColumnVisibilityStore, useSelectedKeys, ColumnVisibilityStore };

